import React, { useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import LazyHydrate from 'react-lazy-hydration'
import { Button } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { ParentalControlsIcon } from '@/atoms/Icons/ParentalControlsIcon'
import { PlayCircleOutlineIcon } from '@/atoms/Icons/PlayCircleOutlineIcon'
import { ModalSheet } from '@/atoms/ModalSheet'
import { CaptionMD, CaptionSM, LabelMD, ParagraphMD, ParagraphSM, TitleMD } from '@/atoms/Text'
import { SmpteController } from '@/organisms/AngelPlayer'
import { useBotDetection } from '@/services/BotDetection'
import { useGuildMember } from '@/services/GuildUserService'
import { useUserRegion } from '@/services/UserRegionService'
import { isGuildFullySupported } from '@/utils/GuildUtil'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, TranslateFunction, useTranslate } from '@/utils/translate/translate-client'

interface ParentalControlsProps {
  controller: SmpteController | undefined
  episodeName: string
  projectSlug: string
  setIsModalOpen: (isOpen: boolean) => void
  isModalOpen: boolean
  userEnabledParentalControls: boolean
  setUserSwearingEnabledPreference?: (swearingPreference: boolean) => Promise<null>
}

export const ParentalControlsModal: React.FC<ParentalControlsProps> = ({
  controller,
  episodeName,
  isModalOpen = false,
  setIsModalOpen,
  setUserSwearingEnabledPreference,
  userEnabledParentalControls,
  projectSlug,
}) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const { isGuildMember, loading } = useGuildMember()
  const { t } = useTranslate('common')
  const { region } = useUserRegion()
  const { isBot } = useBotDetection()
  const track = useSafeTrack()

  const isGuildSupported = isGuildFullySupported(region)
  const shouldRender = isGuildSupported && !isBot

  useEffect(() => {
    if (isModalOpen && controller) {
      controller.pause()
    }
  }, [isModalOpen, controller])

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false)
  }, [setIsModalOpen])

  const handleClickOutside = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (isModalOpen && ref.current && !ref.current.contains(event.target as Node)) {
        handleModalClose()
      }
    },
    [ref, handleModalClose, isModalOpen],
  )

  const handleStartWatchingClick = useCallback(
    (swearingPreference: boolean) => {
      track('Parental Controls Updated', {
        swearingPreference,
        projectSlug,
      })
      setUserSwearingEnabledPreference?.(swearingPreference)
      handleModalClose()
    },
    [handleModalClose, setUserSwearingEnabledPreference, projectSlug, track],
  )

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [handleClickOutside])

  if (!shouldRender) return null

  return (
    <LazyHydrate whenVisible>
      <>
        <ModalSheet
          isOpen={isModalOpen}
          onClose={handleModalClose}
          sheetHeight="auto"
          backgroundScreenColor="black"
          panelClasses="md:min-w-[88vw] lg:min-w-min !p-4 !py-0 md:!py-9 !max-w-[834px] !bg-core-gray-950"
        >
          <div className="pt-4 md:pt-0">
            <ParentalControlsBody
              episodeName={episodeName}
              isGuildMember={isGuildMember}
              t={t}
              handleModalClose={handleModalClose}
              handleStartWatchingClick={handleStartWatchingClick}
              userEnabledParentalControls={userEnabledParentalControls}
              loading={loading}
            />
          </div>
        </ModalSheet>
      </>
    </LazyHydrate>
  )
}

interface ParentalControlsBodyProps {
  episodeName: string
  isGuildMember: boolean
  loading: boolean
  t: TranslateFunction
  userEnabledParentalControls: boolean
  handleModalClose: () => void
  handleStartWatchingClick: (swearingPreference: boolean) => void
}

const ParentalControlsBody: React.FC<ParentalControlsBodyProps> = ({
  episodeName,
  t,
  handleModalClose,
  handleStartWatchingClick,
  userEnabledParentalControls,
}) => {
  type AudioPreference = 'clean' | 'original'
  const [audioPreference, setAudioPreference] = useState<AudioPreference>(
    userEnabledParentalControls ? 'clean' : 'original',
  )

  interface RadioOptionProps {
    value: AudioPreference
    label: string
    badgeText?: string
    selected: boolean
    onSelect: (value: AudioPreference) => void
  }

  const RadioOption: React.FC<RadioOptionProps> = ({ value, label, badgeText, selected, onSelect }) => {
    return (
      <label className="block w-full cursor-pointer">
        <input
          type="radio"
          name="audioPreference"
          className="sr-only"
          checked={selected}
          onChange={() => onSelect(value)}
          aria-checked={selected}
          aria-label={label}
        />
        <div
          className={classNames(
            'flex items-center justify-between gap-3 rounded-2xl px-4 py-4 transition-colors',
            selected
              ? 'border-2 border-oxide-bright bg-core-gray-900/40'
              : 'border border-core-gray-800 hover:border-core-gray-800',
          )}
        >
          <div className="flex items-center gap-2">
            <CaptionMD weight="bold">{label}</CaptionMD>
            {badgeText ? (
              <ParagraphSM className="inline-flex items-center rounded-full bg-cool-01 px-3 py-2 uppercase leading-none !text-cool-03">
                {badgeText}
              </ParagraphSM>
            ) : null}
          </div>
          <div
            className={classNames(
              'grid h-6 w-6 place-items-center rounded-full border-2 transition-colors',
              selected ? 'border-oxide-bright bg-oxide-bright/10' : 'border-core-gray-600',
            )}
          >
            <span
              className={classNames(
                'block h-3.5 w-3.5 rounded-full transition-colors',
                selected ? 'bg-oxide-bright' : 'bg-transparent',
              )}
            />
          </div>
        </div>
      </label>
    )
  }

  return (
    <div className="flex flex-col bg-core-gray-950 text-white">
      <div className="flex flex-col gap-y-4 md:gap-y-6 md:px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ParentalControlsIcon />
            <TitleMD weight="bold">{t('parentalControls', 'Parental Controls')}</TitleMD>
          </div>
          <div
            onClick={handleModalClose}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white/20"
          >
            <CloseIcon color="white" size={16} className="cursor-pointer" />
          </div>
        </div>
        <div className="flex flex-col items-start border-l-2 border-warning-400 pl-4">
          <CaptionMD>{t('contentAdvisory', 'Content Advisory')}</CaptionMD>
          <CaptionSM color="core-gray-400" className="pt-1">
            <Translate t={t} i18nKey="contentAdvisoryDescription" values={{ episodeName }}>
              {{ episodeName }} contains moderate language. Two audio preferences are available below.
            </Translate>
          </CaptionSM>
        </div>
        <ParagraphMD color="core-gray-400">
          {t('selectPreferredAudio', 'Select your preferred audio experience')}
        </ParagraphMD>

        <div className="flex flex-col gap-6">
          <RadioOption
            value="clean"
            label={t('skipProfanity', 'Skip Profanity')}
            badgeText={t('beta', 'BETA')}
            selected={audioPreference === 'clean'}
            onSelect={setAudioPreference}
          />
          <RadioOption
            value="original"
            label={t('originalAudio', 'Original Audio')}
            selected={audioPreference === 'original'}
            onSelect={setAudioPreference}
          />
        </div>
        <ParagraphSM color="core-gray-500" className="text-[11px]">
          *Only available for English and Spanish audio playback.
        </ParagraphSM>
        <Button
          variant="white"
          onClick={() => handleStartWatchingClick(audioPreference === 'clean')}
          className="mb-4 flex max-h-12 w-full items-center gap-[6px] rounded-xl px-6 py-[14px]"
        >
          <PlayCircleOutlineIcon color="black" />
          <LabelMD weight="semibold" className="mt-[2.25px]" color="core-gray-950">
            {t('startWatching', 'Start Watching')}
          </LabelMD>
        </Button>
      </div>
    </div>
  )
}
